/* eslint-disable no-useless-escape */
import { zodResolver } from "@hookform/resolvers/zod";

import React, { useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import Select from "react-select";
import { z } from "zod";

import { SubmitButton as Button } from "~/components/form/submit-button";
import {
  addReasonCode,
  getReasonCodeById,
  getUserMaster,
  EditReasonCode,
} from "~/services/supervisor.services";

const baseSchema = z.object({
  reason_code: z
    .string()
    .min(1,{
      message: "This field must contain at least 1 character(s)",
    })
    .regex(/^[^<>()|'\"--#\-\[\]]*$/, {
      message: "Reason Code cannot contain these special characters",
    }),
  reason_type: z.array(z.number()).min(1),
  document_type: z.array(z.number()).optional(),
  query: z
    .string()
    .min(1,{
      message: "This field must contain at least 1 character(s)",
    })
    .regex(/^[^<>()|'\"--#\-\[\]]*$/, {
      message: "Query cannot contain these special characters",
    }),
  error_category: z
    .string()
    .max(10)
    .regex(/^[^<>()|'\"--#\-\[\]]*$/, {
      message: "Error Category cannot contain these special characters",
    }),
  error_sub_category: z.string().regex(/^[^<>()|'\"--#\-\[\]]*$/, {
    message: "Error Sub-Category cannot contain these special characters",
  }),
});

const createSchema = (isQcErrorSelected: boolean) => {
  return z.object({
    reason_code: baseSchema.shape.reason_code,
    reason_type: baseSchema.shape.reason_type,
    document_type: isQcErrorSelected
      ? z.array(z.number()).optional()
      : z.array(z.number()).min(1),
    query: baseSchema.shape.query,
    error_category: isQcErrorSelected
      ? baseSchema.shape.error_category
      : baseSchema.shape.error_category.optional(),
    error_sub_category: isQcErrorSelected
      ? baseSchema.shape.error_sub_category
      : baseSchema.shape.error_sub_category.optional(),
  });
};

type FormValues = z.infer<typeof baseSchema>;
interface OptionType {
  label: string;
  value: number;
}
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  id?: number;
}

const SuccessDialog: React.FC<{ message: string; onClose: () => void }> = ({
  message,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md mx-auto">
        <h3 className="text-lg font-bold text-center mb-4">Success</h3>
        <p className="text-center mb-4">{message}</p>
      </div>
    </div>
  );
};

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, id }) => {
  const reasonCodeRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [reasonTypes] = useState<OptionType[]>([
    { label: "Exception", value: 0 },
    { label: "QC Error", value: 1 },
  ]);

  const [documentTypes, setDocumentTypes] = useState<OptionType[]>([]);
  const [isDocumentTypeEnabled, setIsDocumentTypeEnabled] = useState(true);
  const [isReasonCodeEnabled, setReasonCodeEnabled] = useState(true);
  const [isQcErrorSelected, setIsQcErrorSelected] = useState(false);

  const defaultValues: FormValues = {
    reason_code: "",
    reason_type: [],
    document_type: [],
    query: "",
    error_category: "",
    error_sub_category: "",
  };

  const [validationSchema, setValidationSchema] = useState(() =>
    createSchema(isQcErrorSelected)
  );

  const demoForm = useForm<FormValues>({
    mode: "onChange",
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const { handleSubmit, control, watch, reset, setValue } = demoForm;
  const reasonTypeWatch = watch("reason_type") || [];

  const fetchFormData = async (id: number) => {
    const { data, status } = await getReasonCodeById(id);
    console.log(data);

    if (status) {
      const reasonCode = (data as any).data.reasoncode_details.reasoncode;
      const reasonTypeValue = (data as any).data.reasoncode_details.reasontype;
      const query = (data as any).data.reasoncode_details.description;

      const errorCategory = (data as any).data.reasoncode_details.errorcategory;
      const errorSubCategory = (data as any).data.reasoncode_details
        .errorsubcategory;

      const reasonTypeArray = Array.isArray(reasonTypeValue)
        ? reasonTypeValue
        : [reasonTypeValue];

      const reasoncodeDoctype = (data as any).data.reasoncode_doctype || [];
      const documentTypeArray = reasoncodeDoctype.map(
        (item: { docid: any }) => item.docid
      );
      console.log(documentTypeArray);
      setValue("reason_code", reasonCode);
      setValue("reason_type", reasonTypeArray);
      setValue("query", query);
      setValue("document_type", documentTypeArray);

      setValue("error_category", errorCategory);
      setValue("error_sub_category", errorSubCategory);
    }
  };
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const onSubmit = async (data: FormValues) => {
    const reasonTypeValue =
      data.reason_type.length > 0 ? data.reason_type[0] : null;

    const updatedData = {
      ...data,
      reason_type: reasonTypeValue,
    };

    if (id) {
      // If id is present, update the existing record
      const { status } = await EditReasonCode(updatedData, id);
      if (status) {
        setSuccessMessage("Reason Code has been updated successfully");

        setTimeout(() => {
          reset(defaultValues);
          onClose();
          window.location.reload();
        }, 3000);
      }
    } else {
      const { status } = await addReasonCode(updatedData);
      if (status) {
        setSuccessMessage("New Reason Code has been created successfully");

        setTimeout(() => {
          reset(defaultValues);
          onClose();
          window.location.reload();
        }, 3000);
      }
    }
  };
  async function fetchUsers() {
    const { data: docData, status: docStatus } =
      await getUserMaster("doc_types");
    if (docStatus) {
      const docTypeOptions = docData.data.map((doc: any) => ({
        label: doc.DocType,
        value: doc.DocId,
      }));
      setDocumentTypes(docTypeOptions);
    }
  }
  useEffect(() => {
    void fetchUsers();
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (id) {
        void fetchFormData(id);
      } else {
        reset(defaultValues);
      }
    }
  }, [isOpen, id, reset]);

  useEffect(() => {
    const isQcError = reasonTypeWatch.includes(1);

    setIsQcErrorSelected(isQcError);

    const enableValues = [0, 2];
    const shouldEnable = reasonTypeWatch.some((value) =>
      enableValues.includes(value)
    );
    setIsDocumentTypeEnabled(shouldEnable);
    if (id != 0) {
      setReasonCodeEnabled(false);
    } else {
      setReasonCodeEnabled(true);

      reasonCodeRef.current?.focus();
    }
    if (!isQcError) {
      setValue("error_category", "");
      setValue("error_sub_category", "");
    } else {
      setValue("document_type", []);
    }

    setValidationSchema(createSchema(isQcError));
  }, [reasonTypeWatch]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl">
        <h2 className="text-xl font-bold mb-4 flex justify-center">
          {id ? "Edit Reason Code" : "Add New Reason Code"}
        </h2>

        {successMessage && (
          <SuccessDialog
            message={successMessage}
            onClose={() => {
              setSuccessMessage(null);
              onClose();
            }}
          />
        )}

        <FormProvider {...demoForm}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-2 gap-4">
              <div className="">
                <label className="block text-sm font-medium text-gray-700">
                  Reason Code <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="reason_code"
                  control={control}
                  render={({ field }) => (
                    <>
                      <input
                        {...field}
                        style={{ width: "100%" }}
                        className={`block h-10 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!isReasonCodeEnabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        placeholder="Reason Code"
                        disabled={!isReasonCodeEnabled}
                      />
                      <div className="mt-1 min-h-[24px] min-w-[360px] abc">
                        {demoForm.formState.errors.reason_code && (
                          <p className="text-red-500 text-sm mt-1 rv">
                            {demoForm.formState.errors.reason_code?.message}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Reason Type <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="reason_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      styles={{
                        container: (provided: any) => ({
                          ...provided,
                          width: "300px",
                        }),
                        menu: (provided: any) => ({
                          ...provided,
                          width: "300px",
                        }),
                      }}
                      value={reasonTypes.filter((option) =>
                        field.value.includes(option.value)
                      )}
                      options={reasonTypes}
                      onChange={(selectedOption: any) => {
                        const newValue = selectedOption
                          ? [selectedOption.value]
                          : [];
                        field.onChange(newValue);
                      }}
                    />
                  )}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Document Type{" "}
                  {isQcErrorSelected ? null : (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <Controller
                  name="document_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      styles={{
                        container: (provided: any) => ({
                          ...provided,
                          width: "300px",
                        }),
                        menu: (provided: any) => ({
                          ...provided,
                          width: "300px",
                        }),
                      }}
                      value={documentTypes.filter((option: any) =>
                        (field?.value || []).includes(option?.value)
                      )}
                      options={documentTypes}
                      onChange={(selectedOptions: any) => {
                        const newValue = selectedOptions
                          ? selectedOptions.map((opt: any) => opt.value)
                          : [];
                        field.onChange(newValue);
                      }}
                      isMulti
                      isDisabled={!isDocumentTypeEnabled}
                    />
                  )}
                />
              </div>

              <div className="mb-4">
                <label
                  className={`block text-sm font-medium  ${isQcErrorSelected ? "text-gray-700" : "text-gray-700"}`}
                >
                  {isQcErrorSelected ? "Error Type" : "Query"}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="query"
                  control={control}
                  render={({ field }) => (
                    <>
                      <textarea
                        {...field}
                        style={{ width: "100%" }}
                        className="mt-1 block border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder={isQcErrorSelected ? "Error Type" : "Query"}
                        rows={4}
                      />
                      <div className="mt-1 min-h-[24px] min-w-[360px]">
                        {demoForm.formState.errors.query && (
                          <p className="text-red-500 text-sm mt-1">
                            {demoForm.formState.errors.query?.message}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                />
              </div>

              <div className="mb-4">
                <label
                  className={`block text-sm font-medium ${isQcErrorSelected ? "text-gray-700" : "text-gray-700"}`}
                >
                  Error Category{" "}
                  {isQcErrorSelected && <span className="text-red-500">*</span>}
                </label>
                <Controller
                  name="error_category"
                  control={control}
                  render={({ field }) => (
                    <>
                      <input
                        {...field}
                        style={{ width: "100%" }}
                        className={`mt-1 block h-10 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!isQcErrorSelected ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        placeholder="Error Category"
                        disabled={!isQcErrorSelected}
                        max={10}
                      />
                      <div className="mt-1 min-h-[24px] min-w-[360px]">
                        {demoForm.formState.errors.error_category && (
                          <p className="text-red-500 text-sm mt-1">
                            {demoForm.formState.errors.error_category?.message}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                />
              </div>

              <div className="mb-4">
                <label
                  className={`block text-sm font-medium ${isQcErrorSelected ? "text-gray-700" : "text-gray-700"}`}
                >
                  Error Sub-Category{" "}
                  {isQcErrorSelected && <span className="text-red-500">*</span>}
                </label>
                <Controller
                  name="error_sub_category"
                  control={control}
                  render={({ field }) => (
                    <>
                      <input
                        {...field}
                        style={{ width: "100%" }}
                        className={`mt-1 block h-10 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!isQcErrorSelected ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        placeholder="Error Sub-Category"
                        disabled={!isQcErrorSelected}
                      />
                      <div className="mt-1 min-h-[24px] min-w-[384px]">
                        {demoForm.formState.errors.error_sub_category && (
                          <p className="text-red-500 text-sm mt-1">
                            {
                              demoForm.formState.errors.error_sub_category
                                ?.message
                            }
                          </p>
                        )}
                      </div>
                    </>
                  )}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                role="button"
                variant="secondary"
                radius="full"
                size="medium"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button role="button" radius="full" size="medium" type="submit">
                Submit
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default Modal;
